import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import { RootState, AppDispatch, GetState } from '@SonarCX/store'
import { getMedia } from '@SonarCX/lib/api'

export const EMAIL_FILE_EXTENSION = '.SONAR_EMAIL'

export type EmailType = {
  sender: string
  subject: string
  recipient: string
  receivedTime: string
  body: string
}

export type EmailWrapperType = {
  id: string
  email: EmailType
  file: File
}

type ExtensionAPIState = {
  emails: Record<string, EmailWrapperType>
  loadedEmails: Record<string, EmailType>
}

const initialState: ExtensionAPIState = {
  emails: {},
  loadedEmails: {}
}

const extensionAPISlice = createSlice({
  name: 'extensionAPI',
  initialState,
  reducers: {
    addEmail: (state: ExtensionAPIState, action: PayloadAction<EmailType>) => {
      const email = action.payload
      const id = uuidv4()
      const file = new File(
        [JSON.stringify(email)],
        `${id}${EMAIL_FILE_EXTENSION}`,
        {
          type: 'text/plain'
        }
      )
      state.emails[id] = { id, email, file }
    },

    removeEmail: (state: ExtensionAPIState, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.emails[id]
    },

    addLoadedEmail: (
      state: ExtensionAPIState,
      action: PayloadAction<{ id: string; email: EmailType }>
    ) => {
      const { id, email } = action.payload
      state.loadedEmails[id] = email
    }
  }
})

const { addLoadedEmail } = extensionAPISlice.actions
export const { addEmail, removeEmail } = extensionAPISlice.actions

export const loadEmailContent = (email: Media) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { id } = email
    const { loadedEmails } = selectExtensionAPI(getState())

    if (id in loadedEmails) {
      return
    }

    const file = await getMedia(id)
    const reader = new FileReader()
    reader.onload = () => {
      const parsed: EmailType = JSON.parse(reader.result as string)
      dispatch(addLoadedEmail({ id, email: parsed }))
    }
    reader.readAsText(file)
  }
}

export const selectExtensionAPI = (state: RootState) => state.extensionAPI

export default extensionAPISlice.reducer
